export interface WebSocketMessage {
  type: 'new_user_message' | 'user_online' | 'user_offline' | 'typing' | 'read' | 'sessions' | 'connection_established' | 'session_chat' | 'customer_service_message' | 'send_image_message' | 'image_message_sent' | 'get_quick_replies' | 'quick_replies' | 'get_mp_images' | 'mp_images' | 'mark_as_read' | 'message_marked_as_read'
  userId?: string
  content?: string | object
  timestamp?: string
  page?: number
  data?: SessionsResponse | SessionChatResponse
  message?: string | object
  userid?: string
  user_id?: number
  app_name?: string
  app_name_display?: string
  replies?: QuickReply[]
  content_type?: string
  offset?: number
  count?: number
  images?: any[]
  material_type?: string
  message_id?: string
  read_at?: string
  keyword?: string
  image_base64?: string
  media_id?: string
  url?: string
  status?: string
}

export interface QuickReply {
  id: number
  name: string
  content: string
  message_type: string
  category: string
  category_display: string
  use_count: number
  created_at: string
}

export interface Session {
  session_id: string
  userid: string
  status: 'active' | 'inactive' | 'closed'
  channel: number
  start_time: string
  end_time: string | null
  customer_service_id: string
  latest_message: object
  latest_message_time: string
  message_count: number
  user_message_count: number
  app_name?: string
  app_name_display?: string
}

export interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  has_previous: boolean
  has_next: boolean
  previous_page_number: number | null
  next_page_number: number | null
}

export interface SessionsResponse {
  sessions: Session[]
  pagination: Pagination
}

export interface User {
  id: string
  name: string
  avatar: string
  lastMessage: string
  lastMessageTime: string
  unreadCount: number
  status: 'online' | 'offline' | 'away'
  app_name?: string
  app_name_display?: string
}

export interface ChatMessage {
  id: string
  userid: string
  content: object,
  content_type: string,
  message_type: 'user' | 'bot' | 'customer_service' | 'system',
  channel: number
  kfstate: number
  assessment: number
  event: any
  customer_info: any
  created_at: string
  message_type_text: string
  channel_text: string
  kfstate_text: string
}

export interface SessionChatResponse {
  userid: string
  messages: ChatMessage[]
  pagination: Pagination
  session: {
    session_id: string
    status: string
    start_time: string
    end_time: string | null
    customer_service_id: string
  }
}

import { getWebSocketUrl } from '../config/environment'

class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 3000
  private messageHandlers: ((message: WebSocketMessage) => void)[] = []
  private connectionHandlers: ((connected: boolean) => void)[] = []
  private messageQueue: WebSocketMessage[] = [] // 消息队列
  private isConnected = false // 连接状态标志
  private lastSessionsRequest: { page: number; timestamp: number } | null = null // 记录最后一次sessions请求
  private sessionsRequestCooldown = 1000 // 1秒冷却时间
  private activeComponents = new Set<string>() // 跟踪活跃的组件
  private connectionPromise: Promise<void> | null = null // 连接Promise
  private isConnecting = false // 连接中状态标志

  // 模拟用户数据
  private mockUsers: User[] = [
    {
      id: '1',
      name: '张三',
      avatar: 'https://gw.alicdn.com/tfs/TB1NvBELpzqK1RjSZFCXXbbxVXa-200-200.png',
      lastMessage: '您好，我想咨询一下订单问题',
      lastMessageTime: '14:30',
      unreadCount: 2,
      status: 'online'
    },
    {
      id: '2',
      name: '李四',
      avatar: 'https://gw.alicdn.com/tfs/TB1NvBELpzqK1RjSZFCXXbbxVXa-200-200.png',
      lastMessage: '退款什么时候到账？',
      lastMessageTime: '14:25',
      unreadCount: 0,
      status: 'online'
    },
    {
      id: '3',
      name: '王五',
      avatar: 'https://gw.alicdn.com/tfs/TB1NvBELpzqK1RjSZFCXXbbxVXa-200-200.png',
      lastMessage: '物流信息查询',
      lastMessageTime: '14:20',
      unreadCount: 1,
      status: 'away'
    },
    {
      id: '4',
      name: '赵六',
      avatar: 'https://gw.alicdn.com/tfs/TB1NvBELpzqK1RjSZFCXXbbxVXa-200-200.png',
      lastMessage: '商品质量问题',
      lastMessageTime: '14:15',
      unreadCount: 0,
      status: 'offline'
    }
  ]

  connect(url?: string) {
    // 如果没有提供 URL，使用环境配置中的默认 URL
    const baseWsUrl = url || getWebSocketUrl()
    // 如果已经连接或正在连接，则直接返回
    if (this.isConnected || this.isConnecting) {
      console.log('WebSocket 已经连接或正在连接中，跳过重复连接')
      return
    }

    // 检查是否有token
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('没有找到token，无法建立WebSocket连接')
      return
    }

    try {
      console.log('开始建立WebSocket连接，URL:', baseWsUrl)
      this.isConnecting = true
      
      // 创建真实的WebSocket连接，拼装customer-service路径和token
      const wsUrlWithToken = `${baseWsUrl}customer-service/${token}`
      this.ws = new WebSocket(wsUrlWithToken)
      
      this.ws.onopen = () => {
        console.log('WebSocket 连接成功')
        this.isConnecting = false
        this.notifyConnectionChange(true)
        this.reconnectAttempts = 0
        this.isConnected = true
        this.processMessageQueue() // 连接成功后处理消息队列
        
        // 发送连接建立消息
        const connectionMessage: WebSocketMessage = {
          type: 'connection_established',
          timestamp: new Date().toISOString()
        }
        this.handleMessage(connectionMessage)
      }
      
      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          console.log('收到消息:', message)
          this.handleMessage(message)
        } catch (error) {
          console.error('解析消息失败:', error)
        }
      }
      
      this.ws.onclose = (event) => {
        console.log('WebSocket 连接关闭:', event.code, event.reason)
        this.isConnecting = false
        this.notifyConnectionChange(false)
        this.isConnected = false
        
        // 如果不是主动关闭，尝试重连
        if (event.code !== 1000) {
          this.handleReconnect()
        }
      }
      
      this.ws.onerror = (error) => {
        console.error('WebSocket 连接错误:', error)
        this.isConnecting = false
        this.notifyConnectionChange(false)
        this.isConnected = false
      }

    } catch (error) {
      console.error('WebSocket 连接失败:', error)
      this.isConnecting = false
      this.handleReconnect()
    }
  }

  private handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`尝试重新连接... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
      
      setTimeout(() => {
        this.connect() // 使用环境配置的默认 URL
      }, this.reconnectInterval)
    } else {
      console.error('WebSocket 重连失败，已达到最大重试次数')
    }
  }

  disconnect() {
    console.log('WebSocketService.disconnect 被调用，当前活跃组件数量:', this.activeComponents.size)
    
    // 只有当没有活跃组件时才真正断开连接
    if (this.activeComponents.size === 0) {
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
      this.isConnected = false
      this.isConnecting = false
      this.messageQueue = [] // 清空消息队列
      console.log('WebSocket 已断开连接，消息队列已清空')
    } else {
      console.log('还有活跃组件，保持WebSocket连接')
    }
  }

  send(message: WebSocketMessage) {
    console.log('WebSocketService.send 被调用，消息:', message, '连接状态:', this.isConnected, 'readyState:', this.ws?.readyState)
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket发送消息:', message)
      this.ws.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket 未连接，将消息加入队列，状态:', this.isConnected, this.ws?.readyState)
      this.messageQueue.push(message) // 将消息添加到队列
      console.log('消息已加入队列，当前队列长度:', this.messageQueue.length)
    }
  }

  onMessage(handler: (message: WebSocketMessage) => void) {
    // 检查是否已经注册过相同的处理器
    if (!this.messageHandlers.includes(handler)) {
      this.messageHandlers.push(handler)
      console.log('注册消息处理器，当前处理器数量:', this.messageHandlers.length, '处理器:', handler.toString().slice(0, 100))
    } else {
      console.log('消息处理器已存在，跳过重复注册')
    }
  }

  // 添加移除消息监听器的方法
  removeMessageHandler(handler: (message: WebSocketMessage) => void) {
    const index = this.messageHandlers.indexOf(handler)
    if (index > -1) {
      this.messageHandlers.splice(index, 1)
      console.log('移除消息处理器，当前处理器数量:', this.messageHandlers.length)
    }
  }

  onConnectionChange(handler: (connected: boolean) => void) {
    // 检查是否已经注册过相同的处理器
    if (!this.connectionHandlers.includes(handler)) {
      this.connectionHandlers.push(handler)
      console.log('注册连接状态处理器，当前处理器数量:', this.connectionHandlers.length)
    } else {
      console.log('连接状态处理器已存在，跳过重复注册')
    }
  }

  // 添加移除连接状态监听器的方法
  removeConnectionHandler(handler: (connected: boolean) => void) {
    const index = this.connectionHandlers.indexOf(handler)
    if (index > -1) {
      this.connectionHandlers.splice(index, 1)
      console.log('移除连接状态处理器，当前处理器数量:', this.connectionHandlers.length)
    }
  }

  private handleMessage(message: WebSocketMessage) {
    console.log('处理消息:', message)
    console.log('message.type:', message.type, 'messageHandlers数量:', this.messageHandlers.length)
    // 处理connection_established消息
    if (message.type === 'connection_established') {
      console.log('WebSocket连接已建立')
    }
    
    // 处理sessions类型的消息
    if (message.type === 'sessions' && message.data && 'sessions' in message.data) {
      this.updateSessions((message.data as SessionsResponse).sessions)
    }
    
    // 处理session_chat类型的消息
    if (message.type === 'session_chat' && message.data) {
      console.log('WebSocketService收到session_chat响应:', message.data)
    }
    
    // 通知所有消息处理器
    this.messageHandlers.forEach(handler => handler(message))
  }

  private notifyConnectionChange(connected: boolean) {
    this.connectionHandlers.forEach(handler => handler(connected))
  }

  private processMessageQueue() {
    if (this.isConnected && this.messageQueue.length > 0) {
      console.log('WebSocket 已连接，处理消息队列...')
      while (this.messageQueue.length > 0) {
        const message = this.messageQueue.shift()
        if (message) {
          this.send(message)
        }
      }
      console.log('消息队列处理完成，当前队列长度:', this.messageQueue.length)
    }
  }

  // 获取用户列表
  getUsers(): User[] {
    return [...this.mockUsers]
  }

  // 通过WebSocket获取用户列表
  getSessions(page: number = 1, keyword?: string) {
    console.log('WebSocketService.getSessions 被调用，页码:', page, '关键词:', keyword)
    
    // 检查是否在冷却时间内重复请求（只对相同页码进行冷却）
    const now = Date.now()
    // if (this.lastSessionsRequest && 
    //     this.lastSessionsRequest.page === page && 
    //     now - this.lastSessionsRequest.timestamp < this.sessionsRequestCooldown) {
    //   console.log('sessions请求在冷却时间内，跳过重复请求，页码:', page)
    //   return
    // }
    
    // 记录这次请求
    this.lastSessionsRequest = { page, timestamp: now }
    
    const message: WebSocketMessage = {
      type: 'sessions',
      page: page,
      keyword: keyword,
      timestamp: new Date().toISOString()
    }
    console.log('发送sessions请求:', message, '当前连接状态:', this.isConnected)
    this.send(message)
  }

  // 通过WebSocket获取聊天历史
  getSessionChat(userid: string, page: number = 1) {
    console.log('WebSocketService.getSessionChat 被调用，用户ID:', userid, '页码:', page, '连接状态:', this.isConnected)
    const message: WebSocketMessage = {
      type: 'session_chat',
      userid: userid,
      page: page,
      timestamp: new Date().toISOString()
    }
    console.log('发送session_chat请求:', message)
    this.send(message)
  }

  // 通过WebSocket获取快捷回复列表
  getQuickReplies(appName: string) {
    console.log('WebSocketService.getQuickReplies 被调用，应用名称:', appName)
    const message: WebSocketMessage = {
      type: 'get_quick_replies',
      app_name: appName,
      timestamp: new Date().toISOString()
    }
    console.log('发送get_quick_replies请求:', message)
    this.send(message)
  }

  // 通过WebSocket获取素材列表（从 QuickReply 图片类型获取）
  getMpImages(offset: number = 0, count: number = 10, appName: string = 'qrcode') {
    console.log('WebSocketService.getMpImages 被调用，偏移量:', offset, '数量:', count, '应用:', appName)
    const message: WebSocketMessage = {
      type: 'get_mp_images',
      offset: offset,
      count: count,
      app_name: appName,
      timestamp: new Date().toISOString()
    }
    console.log('发送get_mp_images请求:', message)
    this.send(message)
  }

  // 标记消息为已读
  markMessageAsRead(messageId: string) {
    console.log('WebSocketService.markMessageAsRead 被调用，消息ID:', messageId)
    const message: WebSocketMessage = {
      type: 'mark_as_read',
      message_id: messageId,
      timestamp: new Date().toISOString()
    }
    console.log('发送mark_as_read请求:', message)
    this.send(message)
  }

  // 上传并发送图片消息（图片以 base64 传到后端，后端换取微信 media_id 后下发）
  sendImageMessage(userid: string, imageBase64: string) {
    console.log('WebSocketService.sendImageMessage 被调用，用户ID:', userid, '图片大小(字节):', imageBase64.length)
    const message: WebSocketMessage = {
      type: 'send_image_message',
      userid: userid,
      image_base64: imageBase64,
      content_type: 'image',
      timestamp: new Date().toISOString()
    }
    this.send(message)
  }

  // 更新用户列表
  updateSessions(sessions: Session[]) {
    this.mockUsers = sessions.map(s => ({
      id: s.userid,
      name: s.userid, // Placeholder, ideally from user data
      avatar: 'https://gw.alicdn.com/tfs/TB1NvBELpzqK1RjSZFCXXbbxVXa-200-200.png', // Placeholder
      lastMessage: (s.latest_message as any)?.text ? (s.latest_message as any).text : "[图片]",
      lastMessageTime: s.latest_message_time,
      unreadCount: s.user_message_count,
      status: 'online' // Placeholder, ideally from user data
    }))
  }

  // 更新用户状态
  updateUserStatus(userId: string, status: 'online' | 'offline' | 'away') {
    const user = this.mockUsers.find(u => u.id === userId)
    if (user) {
      user.status = status
    }
  }

  // 更新用户未读消息数
  updateUserUnreadCount(userId: string, count: number) {
    const user = this.mockUsers.find(u => u.id === userId)
    if (user) {
      user.unreadCount = count
    }
  }

  // 注册组件
  registerComponent(componentName: string) {
    this.activeComponents.add(componentName)
    console.log(`组件 ${componentName} 已注册，当前活跃组件:`, Array.from(this.activeComponents))
  }

  // 注销组件
  unregisterComponent(componentName: string) {
    this.activeComponents.delete(componentName)
    console.log(`组件 ${componentName} 已注销，当前活跃组件:`, Array.from(this.activeComponents))
    
    // 如果没有活跃组件，可以考虑断开连接
    if (this.activeComponents.size === 0) {
      console.log('没有活跃组件，可以考虑断开WebSocket连接')
      // 这里可以选择是否断开连接，取决于业务需求
    }
  }

  // 获取活跃组件数量
  getActiveComponentsCount(): number {
    return this.activeComponents.size
  }

  // 获取活跃组件列表
  getActiveComponents(): string[] {
    return Array.from(this.activeComponents)
  }

  // 确保连接已建立
  async ensureConnected(): Promise<void> {
    if (this.isConnected) {
      return Promise.resolve()
    }
    
    if (this.connectionPromise) {
      return this.connectionPromise
    }
    
    this.connectionPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket连接超时'))
      }, 5000)
      
      const onConnect = () => {
        clearTimeout(timeout)
        this.connectionPromise = null
        resolve()
      }
      
      this.connectionHandlers.push(onConnect)
      this.connect()
    })
    
    return this.connectionPromise
  }
}

export default new WebSocketService() 